import {gql} from '@apollo/client';

import {assert} from 'Asserts';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import deepCopy from 'lib/deepCopy';
import * as ComplexPolymerActions from 'modbio/complex-polymers/ComplexPolymerActions';
import {
  ComplexPolymerConnection,
  ComplexPolymerType,
  ConnectionGraphQL,
  ResolvedAttachmentGraphQL,
  OligoGraphQL,
  SimplePolymerGraphQL,
} from 'modbio/complex-polymers/ComplexPolymerConstants';
import {
  resolvedAttachmentContainsHelmReference,
  generateEntityAssociationsFromStructure,
  getConnectionInputs,
  getEntityIds,
  getExistingResolvedAttachmentInputs,
} from 'modbio/complex-polymers/ComplexPolymerUtil';

import {doesEdgeMatchComplexPolymerConnection} from '../drag-and-drop/ComplexPolymerDragAndDropUtil';
import {generateBackbonePolymerStructureGraphNodes} from '../GeneratePolymerStructureGraphUtil';
import {PolymerStructureGraph} from '../PolymerStructureGraphConstants';
import {
  ComplexPolymerNewOligoQuery,
  ComplexPolymerNewOligoQueryVariables,
} from './__generated/ComplexPolymerNewOligoQuery';

type SequenceGraphQL = ExtractByTypename<ComplexPolymerNewOligoQuery['sequence'], 'SequenceNode'>;

export const COMPLEX_POLYMER_NEW_OLIGO_QUERY = gql`
  query ComplexPolymerNewOligoQuery($sequenceId: StrictString!)
  @engComponent(name: MODIFIED_BIOLOGICS)
  @skipAudit {
    sequence(id: $sequenceId) {
      ... on SequenceNode {
        id
        name
        iconId
        bases
        modifiedNucleotides {
          position
          sugarMonomerId
          baseMonomerId
          phosphateMonomerId
        }
        nucleotideType
        archiveRecord {
          ...ArchiveRecordGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('ArchiveRecord').fragment}
`;

export function getCurrentOligoId(simplePolymers: Array<SimplePolymerGraphQL>, oligo: OligoGraphQL): string {
  const oligos = simplePolymers.filter(
    (polymer) => polymer.entity.__typename === 'SequenceNode' && polymer.helmReference === oligo.helmReference
  );
  assert(oligos.length === 1, 'Expected exactly one matching sequence');
  assert(
    !isInaccessible(oligos[0].entity),
    'All component entities of complex polymer must be accessible to edit'
  );
  return oligos[0].entity.id;
}

function getUpdatedStructureGraph(
  complexPolymerType: ComplexPolymerType,
  structureGraph: PolymerStructureGraph | null,
  oligoRef: string,
  newOligo: SequenceGraphQL,
  newConnections: Array<ComplexPolymerConnection>,
  offset: number | null
): PolymerStructureGraph | null {
  if (structureGraph == null) {
    return null;
  }

  const newBackboneNodes = generateBackbonePolymerStructureGraphNodes(
    complexPolymerType,
    oligoRef,
    newOligo,
    offset
  );
  const newNodes = structureGraph.nodes.filter((node) => node.data.polymerRef !== oligoRef);

  const newEdges = structureGraph.edges.filter((edge) =>
    newConnections.some((connection) =>
      doesEdgeMatchComplexPolymerConnection(edge, structureGraph.nodes, connection)
    )
  );

  return {
    nodes: newBackboneNodes.concat(newNodes),
    edges: newEdges,
  };
}

export async function swapOligo(
  complexPolymerId: string,
  complexPolymerVersionId: string,
  complexPolymerType: ComplexPolymerType,
  simplePolymers: Array<SimplePolymerGraphQL>,
  connections: Array<ConnectionGraphQL>,
  resolvedAttachments: Array<ResolvedAttachmentGraphQL>,
  oligo: OligoGraphQL,
  selectedIds: Array<string>,
  structureGraph: PolymerStructureGraph | null,
  offset?: number | null
): Promise<void> {
  assert(selectedIds.length === 1, 'Can only set one oligo on a complex polymer');

  const newOligo = await doQuerySafe<ComplexPolymerNewOligoQuery, ComplexPolymerNewOligoQueryVariables>(
    {query: COMPLEX_POLYMER_NEW_OLIGO_QUERY},
    {variables: {sequenceId: selectedIds[0]}}
  );

  const entityIds = getEntityIds(simplePolymers);
  const entityAssociations = generateEntityAssociationsFromStructure(simplePolymers);
  const oligoHelmReference = oligo.helmReference;

  const newEntityAssociations = deepCopy(entityAssociations);
  const currentOligoId = getCurrentOligoId(simplePolymers, oligo);

  if (newEntityAssociations[currentOligoId].length > 1) {
    const refs = newEntityAssociations[currentOligoId].filter(
      (polymerRef) => polymerRef !== oligoHelmReference
    );
    newEntityAssociations[currentOligoId] = refs;
  } else {
    delete newEntityAssociations[currentOligoId];
  }

  // Since this is a swapping action, we re-use the HELM reference of oligo being swapped for the new entity association
  if (newEntityAssociations.hasOwnProperty(selectedIds[0])) {
    newEntityAssociations[selectedIds[0]].push(oligo.helmReference);
  } else {
    newEntityAssociations[selectedIds[0]] = [oligo.helmReference];
  }

  // On an oligo conjugate, all existing connections are removed on swapping oligo
  let newConnections: Array<ComplexPolymerConnection> = [];
  // On an oligo duplex, connections on the strand being swapped are removed, and retained on the strand not swapped out
  if (complexPolymerType === ComplexPolymerType.OLIGO_DUPLEX) {
    const connectionInputs = getConnectionInputs(connections);
    newConnections = connectionInputs.filter(
      (connection) =>
        connection.source.polymer_ref !== oligoHelmReference &&
        connection.target.polymer_ref !== oligoHelmReference
    );
  }

  const newResolvedAttachments = getExistingResolvedAttachmentInputs(
    resolvedAttachments.filter(
      (resolvedAttachment) => !resolvedAttachmentContainsHelmReference(resolvedAttachment, oligoHelmReference)
    )
  );

  // Only a single instance of the current oligo should be removed (there may be multiple instances of the same
  // oligo entity in an oligo duplex, for example)
  const oligoEntityIdx = entityIds.findIndex((entityId) => entityId === currentOligoId);
  const newEntityIds = entityIds.filter((_, idx) => idx !== oligoEntityIdx);
  newEntityIds.push(selectedIds[0]);

  assert(!isInaccessible(newOligo.sequence), 'Expected accessible sequence');
  const updatedStructureGraph = getUpdatedStructureGraph(
    complexPolymerType,
    structureGraph,
    oligoHelmReference,
    newOligo.sequence,
    newConnections,
    offset ?? null
  );

  await ComplexPolymerActions.updateComplexPolymer(
    complexPolymerId,
    complexPolymerVersionId,
    newEntityIds,
    newConnections,
    newResolvedAttachments,
    newEntityAssociations,
    complexPolymerType,
    offset ?? undefined,
    updatedStructureGraph ?? undefined
  );
}
